import {
    assignModelOptions,
    CompositeModel,
    ConfigConstants,
    CustomInjector,
    DisplayDecorator,
    ModelOptionsLoaderService,
    ODataObject,
    RadioModel,
    RadioModelOptions,
    TextModel,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';

export class PIMeasurementToolModel extends CompositeModel {
    public equipmentDesc: TextModel;
    public equipNo: TextModel;
    public materialNo: TextModel;
    public serialNo: TextModel;
    public isEntryDeletable = false;
    public ynFlagRadioModel: RadioModel<TextModel>;
    public comments: TextModel;

    public constructor(data?: OdataPIMeasurementToolModel) {
        super('PIMeasurementToolModel', {});
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const translate = injector.get(TranslateService);

        this.equipmentDesc = <TextModel>this.set(new TextModel('TechObjDescription', data?.TechObjDescription));
        modelOptionsLoader.load(this.equipmentDesc, 'PITools', 'Description');
        this.equipNo = <TextModel>this.set(new TextModel('EquipNo', data?.EquipNo));
        modelOptionsLoader.load(this.equipNo, 'PITools', 'EquipNo');
        this.materialNo = <TextModel>this.set(new TextModel('PartNo', data?.PartNo));
        modelOptionsLoader.load(this.materialNo, 'PITools', 'PartNo');
        // Serial number should be blank by default and CSE has to enter it manually.
        this.serialNo = <TextModel>this.set(new TextModel('SerialNo', ''));
        modelOptionsLoader.load(this.serialNo, 'PITools', 'SerialNo');
        this.comments = <TextModel>this.set(new TextModel('Comments', ''));
        modelOptionsLoader.load(this.comments, 'PITools', 'Comments');
        this.isEntryDeletable = !data;

        const radioOptionsGroup: Array<DisplayDecorator<TextModel>> = [];
        radioOptionsGroup.push(
            new DisplayDecorator(
                <string>translate.instant('PHYSICAL_INVENTORY_RADIO_OPTIONS.OPTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
            ),
        );
        radioOptionsGroup.push(
            new DisplayDecorator(
                <string>translate.instant('PHYSICAL_INVENTORY_RADIO_OPTIONS.OPTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
            ),
        );
        radioOptionsGroup.push(
            new DisplayDecorator(
                <string>translate.instant('PHYSICAL_INVENTORY_RADIO_OPTIONS.OPTION_NA'),
                new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
            ),
        );
        const optionNAIndex = 2;
        this.ynFlagRadioModel = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'YNFlag',
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: optionNAIndex, invalidSelection: optionNAIndex }),
                ),
            )
        );

        if (this.isEntryDeletable) {
            const optionYesIndex = 0;
            this.ynFlagRadioModel.selectByIndex(optionYesIndex);
            this.ynFlagRadioModel = assignModelOptions(
                this.ynFlagRadioModel,
                {
                    ...this.ynFlagRadioModel.options,
                    readonly: true,
                },
                this,
            );
        }
    }

    public evaluateMandatoryFields(): void {
        this.equipNo = assignModelOptions(
            this.equipNo,
            {
                ...this.equipNo.options,
                required:
                    this.equipNo.value.length === 0 &&
                    this.materialNo.value.length === 0 &&
                    this.serialNo.value.length === 0,
            },
            this,
        );
        this.materialNo = assignModelOptions(
            this.materialNo,
            {
                ...this.materialNo.options,
                required: this.materialNo.value.length === 0 && this.equipNo.value.length === 0,
            },
            this,
        );
    }

    public static fromOdataPIToolsSet(data: OdataPIMeasurementToolModel): PIMeasurementToolModel {
        return new PIMeasurementToolModel(data);
    }

    public getOutModel(): ODataPIToolOutModel {
        const outModel = <ODataPIToolOutModel>super.getOutModel();
        outModel.YNFlag =
            outModel.YNFlag === ConfigConstants.optionValueYes
                ? ConfigConstants.optionYesShort
                : ConfigConstants.optionNoShort;
        return outModel;
    }

    public toJSON(): JsonPIMeasurementToolModel {
        return {
            equipmentDesc: this.equipmentDesc.value,
            equipNo: this.equipNo.value,
            materialNo: this.materialNo.value,
            serialNo: this.serialNo.value,
            isEntryDeletable: this.isEntryDeletable,
            ynFlagRadio: this.ynFlagRadioModel.getSelectedIndex(),
            comments: this.comments.value,
        };
    }
}

export class PIMeasurementToolModelFromJson extends PIMeasurementToolModel {
    public constructor(data: JsonPIMeasurementToolModel) {
        super();

        this.equipmentDesc.value = data.equipmentDesc;
        this.equipNo.value = data.equipNo;
        this.materialNo.value = data.materialNo;
        this.serialNo.value = data.serialNo;
        this.isEntryDeletable = data.isEntryDeletable;
        this.comments.value = data.comments;

        if (!this.isEntryDeletable) {
            this.ynFlagRadioModel.selectByIndex(data.ynFlagRadio);
            this.ynFlagRadioModel.options = {
                ...this.ynFlagRadioModel.options,
                readonly: false,
            };
        }

        if (this.isEntryDeletable) {
            this.evaluateMandatoryFields();
        }
    }
}

export interface JsonPIMeasurementToolModel {
    equipmentDesc: string;
    equipNo: string;
    materialNo: string;
    serialNo: string;
    isEntryDeletable: boolean;
    ynFlagRadio: number;
    comments: string;
}

export interface OdataPIMeasurementToolModel {
    TechObjDescription: string;
    EquipNo: string;
    PartNo: string;
    SerialNo: string;
}

export interface ODataPIToolOutModel extends ODataObject {
    TechObjDescription: string;
    EquipNo: string;
    PartNo: string;
    SerialNo: string;
    Comments: string;
    YNFlag: string;
}
